import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { ApiService } from './api.service';
import { StorageService } from './storage.service';
import {
  AuthResponse,
  CompanyValidationStatus,
  InvitationRequest,
  InvitationResponse,
  User,
} from '@interfaces/auth.interface';
import {
  Observable,
  catchError,
  filter,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { Userpilot } from 'userpilot';

/**
 * Service responsible for managing authentication flows and user state
 * Centralizes authentication logic to improve maintainability and testability
 */
@Injectable({
  providedIn: 'root',
})
export class AuthManagerService {
  // Global counter for authentication attempts
  private globalAuthAttempts = 0;
  // Maximum number of authentication attempts before giving up
  private readonly MAX_AUTH_ATTEMPTS = 3;

  constructor(
    private auth0Service: AuthService,
    private apiService: ApiService,
    private router: Router,
    private storageService: StorageService,
  ) {
    this.setupLogoutBroadcastChannel();
  }

  private logoutChannel = new BroadcastChannel('logout_channel');

  /**
   * Checks if the current route is public
   * @returns True if the route is public, false otherwise
   */
  private isPublicRoute(): boolean {
    return window.location.pathname.startsWith('/public/');
  }

  /**
   * Initializes the authentication flow
   * @returns Observable that completes when the auth flow is initialized
   */
  private initializeAuth$: Observable<{ authenticated: boolean }> | null = null;
  initializeAuth(): Observable<{ authenticated: boolean }> {
    if (this.initializeAuth$) {
      return this.initializeAuth$;
    }

    this.initializeAuth$ = this.auth0Service.isLoading$.pipe(
      filter((loading) => !loading),
      take(1),
      switchMap(() => this.auth0Service.isAuthenticated$),
      switchMap((isAuthenticated) => {
        if (isAuthenticated) {
          return this.handleAuthenticatedState().pipe(
            map(() => ({ authenticated: true })),
          );
        }

        if (this.isPublicRoute()) {
          this.handleUnauthenticatedState();
          return of({ authenticated: false });
        }

        return this.auth0Service.getAccessTokenSilently().pipe(
          map((token) => {
            this.storageService.setToken(token);
            this.globalAuthAttempts = 0;
            return { authenticated: true };
          }),
          catchError((error) => {
            this.globalAuthAttempts++;

            if (this.globalAuthAttempts >= this.MAX_AUTH_ATTEMPTS) {
              console.warn('Max authentication attempts reached');
              this.handleUnauthenticatedState();
              this.router.navigateByUrl('/auth');
            } else {
              this.handleUnauthenticatedState();
            }

            return of({ authenticated: false });
          }),
        );
      }),
      tap(() => {
        this.initializeAuth$ = null;
      }),
      catchError((err) => {
        this.initializeAuth$ = null;
        return of({ authenticated: false });
      }),
      shareReplay(1),
    );

    return this.initializeAuth$;
  }

  /**
   * Handles the unauthenticated state
   * Clears auth data and preserves language preference
   */
  private handleUnauthenticatedState(): void {
    this.storageService.clearAuthData();
  }

  /**
   * Sets up the BroadcastChannel to listen for logout events from other tabs.
   * When a logout event is received, it clears authentication data and redirects to the login page.
   */
  private setupLogoutBroadcastChannel(): void {
    this.logoutChannel.onmessage = (event) => {
      if (event.data === 'logout') {
        if (!this.isPublicRoute()) {
          this.handleUnauthenticatedState();
          this.router.navigateByUrl('/auth');
        }
      }
    };
  }

  /**
   * Handles the authenticated state
   * Gets the token, verifies email, and processes user data
   * @returns Observable that completes when the auth flow is complete
   */
  private handleAuthenticatedState(): Observable<void> {
    let handleAuthenticatedStateAttempts = 0;
    return this.auth0Service.getAccessTokenSilently().pipe(
      tap((token) => {
        this.storageService.setToken(token);
        handleAuthenticatedStateAttempts = 0; // Reset attempts on successful token retrieval
      }),
      switchMap(() => this.auth0Service.idTokenClaims$),
      switchMap((claims) => {
        if (!claims?.email_verified) {
          this.router.navigateByUrl('/email-verification');
          return of(void 0);
        }

        return this.auth0Service.user$.pipe(
          switchMap((user: any) => this.processAuthenticatedUser(user)),
        );
      }),
      catchError((error) => {
        handleAuthenticatedStateAttempts++;
        if (handleAuthenticatedStateAttempts >= 3) {
          this.logout();
          return of(void 0); // Stop further attempts
        }
        this.logout();
        return throwError(() => new Error('Authentication failed'));
      }),
    );
  }

  /**
   * Processes an authenticated user
   * Fetches user data from API and handles navigation
   * @param user The Auth0 user object
   * @returns Observable that completes when processing is done
   */
  private processAuthenticatedUser(user: any): Observable<void> {
    // Use explicit typing for the API response
    return this.apiService.post<Record<string, any>>('auth/', {}).pipe(
      // Convert the response to our expected type
      map((response) => {
        const authResponse: AuthResponse = {
          user: {
            userId: response['user']?.userId,
            email: response['user']?.email,
            nickname: response['user']?.nickname,
            name: response['user']?.name,
            email_verified: user.email_verified,
          },
          defaultCompany: response['defaultCompany']
            ? {
                companyId: response['defaultCompany'].companyId,
                companyName: response['defaultCompany'].companyName,
                validationStatus: response['defaultCompany'].validationStatus,
                agencyCompanyId: response['defaultCompany'].agencyCompanyId,
              }
            : undefined,
          accessToken: response['accessToken'],
          invitationToken: response['invitationToken'],
        };

        return authResponse;
      }),
      map((response) => {
        this.storageService.setToken(response.accessToken);
        this.identifyUserForAnalytics(response);

        const status = response.defaultCompany?.validationStatus || null;

        if (status === 'VERIFIED') {
          this.handleVerifiedCompany(response, user);
        } else {
          this.handleUnverifiedCompany(status, user);
        }
      }),
      // map(() => void 0),
      catchError((error) => {
        return throwError(() => new Error('Failed to process user data'));
      }),
    );
  }

  /**
   * Identifies the user for analytics purposes
   * @param response The authentication response
   */
  private identifyUserForAnalytics(response: AuthResponse): void {
    Userpilot.identify(response.user.userId, {
      email: response.user.email,
      nickname: response.user.nickname,
      name: response.user.name,
      company: response.defaultCompany
        ? {
            id: response.defaultCompany.companyId,
            name: response.defaultCompany.companyName,
          }
        : undefined,
    });

    Userpilot.reload();
  }

  /**
   * Handles a user with a verified company
   * @param response The authentication response
   * @param user The Auth0 user object
   */
  private handleVerifiedCompany(response: AuthResponse, user: User): void {
    if (response.invitationToken) {
      this.handleInvitedUser(response, user);
    } else {
      this.storageService.setCompanyId(response.defaultCompany!.companyId);
      this.storageService.setAgencyCompanyId(
        response.defaultCompany!.agencyCompanyId,
      );
      this.navigateToDestination();
    }
  }

  /**
   * Handles a user that has been invited to a company
   * @param response The authentication response
   * @param user The Auth0 user object
   */
  private handleInvitedUser(response: AuthResponse, user: User): void {
    this.storageService.setCompanyData(response.defaultCompany!);
    this.storageService.setEmailVerified(user.email_verified);
    this.storageService.setCompanyStatus(
      response.defaultCompany!.validationStatus,
    );
    this.storageService.setInvitationToken(response.invitationToken!);
    this.router.navigate(['/account-verification']);
  }

  /**
   * Handles a user with an unverified company
   * @param status The company validation status
   * @param user The Auth0 user object
   */
  private handleUnverifiedCompany(
    status: CompanyValidationStatus,
    user: User,
  ): void {
    this.storageService.setEmailVerified(user.email_verified);
    this.storageService.setCompanyStatus(status);
    this.router.navigate(['/account-verification']);
  }

  /**
   * Navigates to the appropriate destination based on current URL
   */
  private navigateToDestination(): void {
    this.router.navigateByUrl(
      this.router.url === '/'
        ? '/wallet'
        : this.router.parseUrl(this.router.url),
    );
  }

  /**
   * Accepts an invitation to join a company
   * @param token The invitation token
   * @returns Observable with the invitation response
   */
  acceptInvitation(token: string): Observable<InvitationResponse> {
    const request: InvitationRequest = { token };

    return this.apiService
      .post<Record<string, any>>('auth/accept-invitation', request)
      .pipe(
        // Convert the response to our expected type
        map((response) => {
          const invitationResponse: InvitationResponse = {
            defaultCompany: {
              companyId: response['defaultCompany'].companyId,
              companyName: response['defaultCompany'].companyName,
              validationStatus: response['defaultCompany'].validationStatus,
              agencyCompanyId: response['defaultCompany'].agencyCompanyId,
            },
            accessToken: response['accessToken'],
          };
          return invitationResponse;
        }),
        tap((response) => {
          this.storageService.removeInvitationToken();
          this.storageService.setCompanyId(response.defaultCompany.companyId);
        }),
        catchError((error) => {
          console.error(
            {
              level: 'error',
              message: 'Error accepting invitation',
            },
            error.stack,
            'ACCEPT_INVITATION | ERROR',
          );
          return throwError(() => new Error('Failed to accept invitation'));
        }),
      );
  }

  /**
   * Resends the verification email
   * @returns Observable that completes when the email is sent
   */
  resendVerificationEmail(): Observable<any> {
    return this.apiService.post('/auth/verification-email/', {}).pipe(
      tap(() => {
        this.apiService.alertService.success(
          'accountVerification:responseSuccessEmailVerified',
        );
      }),
      catchError((error) => {
        console.error(
          {
            level: 'error',
            message: 'Error resending verification email',
          },
          error.stack,
          'RESEND_VERIFICATION_EMAIL | ERROR',
        );
        return throwError(
          () => new Error('Failed to resend verification email'),
        );
      }),
    );
  }

  /**
   * Logs the user out
   */
  /**
   * Logs the user out
   * @param dueToInactivity Whether the logout is due to user inactivity
   */
  logout(dueToInactivity: boolean = false): void {
    this.globalAuthAttempts = 0;

    // If logout is due to inactivity, set the flag in sessionStorage
    if (dueToInactivity) {
      sessionStorage.setItem('userInactivity', 'true');
    }

    this.storageService.clearAuthData();
    this.auth0Service.logout();
    this.logoutChannel.postMessage('logout');
  }

  /**
   * Checks if the user is authenticated.
   * @returns True if the user is authenticated, false otherwise.
   */
  isAuthenticated(): boolean {
    return this.storageService.getToken() !== null;
  }
}
